import { useDispatch, useSelector } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useTranslation } from "react-i18next";
import { Button, Card, Col, Form, InputGroup, Modal, OverlayTrigger, Row, Tooltip, Badge } from "react-bootstrap";

import "../../Assets/scss/pages/settings.scss";
import NoRecordFound from "../../Components/NoRecordFound";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AddFilterContentService, deleteFilterContentService, getFilterContentService, UpdateFilterContentService } from "../../Services/FilterContentService";

interface PropTypes {
    disabledButton: boolean
}

// component to render settings page
const FilterContentPage = (prop: PropTypes) => {
  // Redux Data
  const { accessToken, currentWebsite, currentUserStatus } = useSelector(
    (store: StoreProptypes) => store
  );

  const { disabledButton } = prop;

  // Functions
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // useState
  const [filterContentListData, setFilterContentListData] = useState<any>();
  const location = useLocation();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (accessToken && currentWebsite) {
      getFilterContentService(accessToken, currentWebsite?.id?.toString(), setFilterContentListData, dispatch, t);
    }
  }, [accessToken, currentWebsite]);

  // Show Card Popup
  const [filterContentLandMark, setFilterContentLandMark] = useState("");
  const [editingfilterContentLandMark, setEditingFilterContentLandMark] = useState<any | null>(null);
  const [showFilterContentPopup, setShowFilterContentPopup] = useState(false);
  const [showDeleteFilterContentPopup, setDeleteFilterContentPopup] = useState(false);
  const [deleteFilterContentID, setDeleteFilterContentID] = useState<number | null>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Common HTML tags suggestions (compact)
  const commonTags = [
    { tag: "section", description: "Section" },
    { tag: "article", description: "Article" },
    { tag: "nav", description: "Nav" },
    { tag: "header", description: "Header" },
    { tag: "footer", description: "Footer" },
    { tag: "main", description: "Main" }
  ];

  const handleCloseFilterContentPopup = () => {
    setShowFilterContentPopup(false);
    resetForm();
  };

  const handleShowFilterContentPopup = () => {
    setShowFilterContentPopup(true);
  };

  const handleShowDeleteFilterContentPopup = (id: number) => {
    setDeleteFilterContentPopup(true);
    id && setDeleteFilterContentID(id);
  };

  const validateLandMarkCommand = () => {
    const newErrors: { [key: string]: string } = {};
    const onlyHtmlRegex = /^(\s*<\/?[a-z][^>]*>\s*)+$/i;

    if (!filterContentLandMark.trim()) {
      newErrors.filterContentLandMark = t("Landmark is required");
    } else if (filterContentLandMark.length > 100) {
      newErrors.filterContentLandMark = t("Max 100 characters");
    } else if (!onlyHtmlRegex.test(filterContentLandMark)) {
      newErrors.filterContentLandMark = t("Valid HTML tag required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFilterContentLandMark("");
    setEditingFilterContentLandMark(null);
    setErrors({});
    setIsSubmitting(false);
  };

  const handleEditFilterContentLandMark = (id: number) => {
    const getSelectedIdData = filterContentListData.find((d: any) => d.id === id);
    if (getSelectedIdData) {
      setFilterContentLandMark(`<${getSelectedIdData?.title}>`);
      setEditingFilterContentLandMark(getSelectedIdData);
      handleShowFilterContentPopup();
    }
  };

  const stripHtmlTags = (value: string) => {
    return value.replace(/[<>]/g, "");
  };

  const saveLandMarkCommand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (accessToken && validateLandMarkCommand() && currentWebsite && !isSubmitting) {
      setIsSubmitting(true);
      
      try {
        if (editingfilterContentLandMark) {
          await UpdateFilterContentService(
            editingfilterContentLandMark.id,
            currentWebsite?.id?.toString(),
            stripHtmlTags(filterContentLandMark),
            accessToken,
            setFilterContentListData,
            setShowFilterContentPopup,
            dispatch,
            t,
            resetForm
          );
          setSuccessMessage(t("Landmark updated!"));
        } else {
          await AddFilterContentService(
            currentWebsite?.id?.toString(),
            stripHtmlTags(filterContentLandMark),
            accessToken,
            setFilterContentListData,
            setShowFilterContentPopup,
            dispatch,
            t,
            resetForm
          );
          setSuccessMessage(t("Landmark added!"));
        }
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 2000);
      } catch (error) {
        console.error("Error saving landmark:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCloseDeleteFilterContentPopup = () => {
    setDeleteFilterContentPopup(false);
    setDeleteFilterContentID(null);
  };

  const handleDeleteFilterContent = async () => {
    if (accessToken && deleteFilterContentID && currentWebsite) {
      await deleteFilterContentService(
        accessToken,
        currentWebsite?.id?.toString(),
        deleteFilterContentID,
        setFilterContentListData,
        setDeleteFilterContentPopup,
        dispatch,
        t
      );
      setSuccessMessage(t("Landmark deleted!"));
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 2000);
    }
  };

  const handleQuickAddTag = (tag: string) => {
    setFilterContentLandMark(`<${tag}>`);
    setErrors({});
  };

  const renderTooltip = (props: any) => (
    <Tooltip id="button-tooltip" {...props}>
      Add HTML tags as landmarks
    </Tooltip>
  );

  return (
    <>
      <div className="filter-content-page">
        {/* Success Alert - Compact */}
        {showSuccessAlert && (
          <div className="alert alert-success alert-dismissible fade show mb-2 py-1 px-3" role="alert" style={{ fontSize: 13 }}>
            <i className="material-symbols-outlined me-1" style={{ fontSize: 16, verticalAlign: 'middle' }}>
              check_circle
            </i>
            {successMessage}
          </div>
        )}

        {/* Header - Compact */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0 fw-semibold">
              <i className="material-symbols-outlined me-1 text-primary" style={{ fontSize: 20, verticalAlign: 'middle' }}>
                flag
              </i>
              {t("Custom Landmarks")}
              {filterContentListData && filterContentListData.length > 0 && (
                <Badge bg="secondary" className="ms-2" style={{ fontSize: 11 }}>
                  {filterContentListData.length}
                </Badge>
              )}
            </h5>
          </div>
          <OverlayTrigger placement="left" overlay={renderTooltip}>
            <Button
              variant="primary"
              size="sm"
              disabled={disabledButton}
              onClick={handleShowFilterContentPopup}
              className="d-flex align-items-center gap-1 px-3 py-1"
              style={{ fontSize: 13 }}
            >
              <i className="material-symbols-outlined" style={{ fontSize: 16 }}>
                add
              </i>
              <span>{t("Add")}</span>
            </Button>
          </OverlayTrigger>
        </div>

        {/* Landmarks Grid - Compact Cards */}
        <div className="landmarks-grid">
          {filterContentListData ? (
            <>
              {filterContentListData.length > 0 ? (
                <Row xs={2} sm={3} md={4} lg={5} className="g-2">
                  {filterContentListData.map((item: any) => {
                    const colors = ['#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0'];
                    const colorIndex = (item.id || 0) % colors.length;
                    
                    return (
                      <Col key={item.id}>
                        <div className="landmark-card-compact border rounded-3 p-2" style={{
                          background: 'white',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          borderLeft: `3px solid ${colors[colorIndex]}`
                        }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <code className="fs-6 fw-semibold" style={{ fontSize: '13px', fontWeight: 600 }}>
                                &lt;{item.title}&gt;
                              </code>
                            </div>
                            <div className="action-buttons d-flex gap-1">
                              <Button
                                variant="link"
                                size="sm"
                                disabled={disabledButton}
                                onClick={() => handleEditFilterContentLandMark(item.id)}
                                className="p-0 text-primary"
                                style={{ textDecoration: 'none', fontSize: 14 }}
                              >
                                <i className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</i>
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                disabled={disabledButton}
                                onClick={() => handleShowDeleteFilterContentPopup(item.id)}
                                className="p-0 text-danger ms-1"
                                style={{ textDecoration: 'none', fontSize: 14 }}
                              >
                                <i className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</i>
                              </Button>
                            </div>
                          </div>
                          <div className="mt-1">
                            <small className="text-muted" style={{ fontSize: 10 }}>
                              {`<${item.title}>...</${item.title}>`}
                            </small>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <div className="empty-state-compact text-center py-4">
                  <i className="material-symbols-outlined text-muted mb-1" style={{ fontSize: 32 }}>
                    flag
                  </i>
                  <p className="text-muted small mb-2">{t("No landmarks")}</p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleShowFilterContentPopup}
                    className="rounded-pill px-3"
                    style={{ fontSize: 12 }}
                  >
                    {t("Add Landmark")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal - Compact */}
      <Modal
        show={showFilterContentPopup}
        onHide={handleCloseFilterContentPopup}
        // size="md"
        backdrop="static"
        centered
        className="landmark-modal-compact"
      >
        <Modal.Header closeButton className="border-0 pt-3 px-3 pb-0">
          <Modal.Title className="fs-6 fw-semibold">
            {editingfilterContentLandMark ? (
              <div className="d-flex align-items-center gap-1">
                <i className="material-symbols-outlined text-primary" style={{ fontSize: 18 }}>edit</i>
                {t("Update Landmark")}
              </div>
            ) : (
              <div className="d-flex align-items-center gap-1">
                <i className="material-symbols-outlined text-primary" style={{ fontSize: 18 }}>add</i>
                {t("Add Landmark")}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-3 pb-3 pt-2">
          <Form onSubmit={saveLandMarkCommand}>
            {/* HTML Tag Input */}
            <div className="mb-3">
              <Form.Label className="small fw-semibold mb-1">
                {t("HTML Tag")}
                <span className="text-danger ms-1">*</span>
              </Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-light" style={{ padding: '4px 8px' }}>
                  <i className="material-symbols-outlined" style={{ fontSize: 16 }}>code</i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="e.g., <section>"
                  type="text"
                  size="sm"
                  onChange={(e) => setFilterContentLandMark(e.target.value)}
                  value={filterContentLandMark}
                  isInvalid={!!errors.filterContentLandMark}
                  style={{ fontSize: 13 }}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid" className="small">
                  {errors.filterContentLandMark}
                </Form.Control.Feedback>
              </InputGroup>
            </div>

            {/* Quick Suggestions - Compact */}
            <div className="mb-3">
              <Form.Label className="small text-muted mb-1">{t("Quick add")}</Form.Label>
              <div className="d-flex flex-wrap gap-1">
                {commonTags.map((tag, index) => (
                  <Button
                    key={index}
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => handleQuickAddTag(tag.tag)}
                    className="rounded-pill px-2"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                  >
                    &lt;{tag.tag}&gt;
                  </Button>
                ))}
              </div>
            </div>

            {/* Preview - Compact */}
            {filterContentLandMark && !errors.filterContentLandMark && (
              <div className="bg-light p-2 rounded-2 mb-2">
                <small className="text-muted d-flex align-items-center gap-1 mb-1">
                  <i className="material-symbols-outlined" style={{ fontSize: 14 }}>visibility</i>
                  {t("Preview")}:
                </small>
                <code className="small text-success" style={{ fontSize: 12 }}>
                  {filterContentLandMark}
                </code>
              </div>
            )}

            {/* Action Buttons - Compact */}
            <div className="d-flex align-content-center justify-content-center gap-2 mt-3">
              <Button
                variant="primary"
                type="submit"
                size="sm"
                disabled={isSubmitting}
                // className="flex-grow-1"
                style={{ fontSize: 13 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-1" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    {t('Saving...')}
                  </>
                ) : editingfilterContentLandMark ? (
                  t('Update')
                ) : (
                  t('Save')
                )}
              </Button>
              <Button
                variant='outline-primary'
                size="sm"
                onClick={handleCloseFilterContentPopup}
                disabled={isSubmitting}
                style={{ fontSize: 13 }}
              >
                {t('Cancel')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal - Compact */}
      <Modal
        show={showDeleteFilterContentPopup}
        onHide={handleCloseDeleteFilterContentPopup}
        size="sm"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton className="border-0 pt-3 px-3 pb-0">
          <Modal.Title className="fs-6 fw-semibold">
            {t("Delete Landmark")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-3 pb-2">
          <p className="small mb-0 text-muted">
            {t("Delete this landmark?")}
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-3 px-3 pt-0">
          <Button
            variant="danger"
            size="sm"
            onClick={handleDeleteFilterContent}
            style={{ fontSize: 12 }}
          >
            {t("Delete")}
          </Button>
          <Button
            variant="light"
            size="sm"
            onClick={handleCloseDeleteFilterContentPopup}
            style={{ fontSize: 12 }}
          >
            {t("Cancel")}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .landmark-card-compact {
          transition: all 0.2s ease;
          background: white;
        }
        
        .landmark-card-compact:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .empty-state-compact {
          background: #f8f9fa;
          border-radius: 8px;
        }
        
        .filter-content-page {
          padding: 0;
        }
        
        /* Compact modal styling */
        .landmark-modal-compact .modal-content {
          border-radius: 12px;
        }
        
        /* Compact form controls */
        .form-control-sm, .input-group-sm > .form-control {
          font-size: 13px;
          padding: 4px 8px;
        }
        
        .input-group-text {
          font-size: 13px;
          padding: 4px 8px;
        }
        
        /* Compact alert */
        .alert {
          padding: 6px 12px;
          margin-bottom: 12px;
        }
        
        /* Better spacing for small screens */
        @media (max-width: 768px) {
          .row > [class*="col-"] {
            padding: 4px;
          }
        }
      `}</style>
    </>
  );
};

export default FilterContentPage;